import React, { useState, useEffect, useRef } from "react";
import { Card, Headline, Paragraph, Flex, InputLabel } from "_styleguide";
import { useSettings } from "_contexts/useSettings";
import { useApolloClient } from "@apollo/client";
import { seSendPhoneVerification, seVerifyPhoneCode } from "_graphql/mutations";
import { message } from "@centrate-io/barn";
import { isDarkColor } from "_helpers/colors";
import { IconLoader2, IconCheck } from "@tabler/icons-react";
import { useGTM } from "_hooks/useGTM";
import "./VerifyPhone.scss";

const CODE_LENGTH = 6;
const RESEND_COUNTDOWN = 30;

function VerifyPhone(props) {
  const client = useApolloClient();
  const { settings } = useSettings();
  const inputRef = useRef(null);

  const [code, setCode] = useState("");
  const [status, setStatus] = useState("idle");
  const [seconds, setSeconds] = useState(RESEND_COUNTDOWN);
  const [intervalId, setIntervalId] = useState(null);

  const { gtmEvent, gtmError } = useGTM();
  const { applicationId, phone, maskedDestination, onSuccess, pageSettings } = props;

  const primaryColor = settings?.primary_color || "#0f766e";

  useEffect(() => {
    startCountdown();
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (code.length === CODE_LENGTH && status === "idle") {
      verifyCode(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const startCountdown = () => {
    if (intervalId) clearInterval(intervalId);
    setSeconds(RESEND_COUNTDOWN);

    const id = setInterval(() => {
      setSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    setIntervalId(id);
  };

  const handleChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH);
    setCode(val);
  };

  const verifyCode = async (fullCode) => {
    setStatus("verifying");

    try {
      const result = await client.mutate({
        mutation: seVerifyPhoneCode,
        variables: {
          application_id: applicationId,
          phone: phone,
          code: fullCode,
        },
      });

      const verify = result?.data?.seVerifyPhoneCode;

      if (verify?.success) {
        gtmEvent("phone_code_verified");
        message.success("Phone verified successfully!");
        onSuccess();
      } else {
        if (verify?.error?.toLowerCase().includes("expired")) {
          setStatus("expired");
          gtmError("phone_code_expired");
          message.error(
            "Verification code has expired. Please request a new code."
          );
        } else {
          gtmError("phone_code_invalid");
          message.error(verify?.error || "Invalid code. Please try again.");
          setStatus("idle");
          setCode("");
          inputRef.current?.focus();
        }
      }
    } catch (err) {
      console.error("Verify error:", err);
      gtmError("phone_code_verify_error");
      message.error("Failed to verify code. Please try again.");
      setStatus("idle");
      setCode("");
      inputRef.current?.focus();
    }
  };

  const resendCode = async () => {
    if (seconds > 0) return;

    setStatus("sending");

    try {
      const result = await client.mutate({
        mutation: seSendPhoneVerification,
        variables: {
          application_id: applicationId,
          phone: phone,
        },
      });

      const send = result?.data?.seSendPhoneVerification;

      if (send?.success) {
        setStatus("sent");
        message.success(`New code sent to ${send.masked_destination}`);

        setTimeout(() => {
          setStatus("idle");
          setCode("");
          startCountdown();
          inputRef.current?.focus();
        }, 1500);
      } else {
        message.error(send?.error || "Failed to send code. Please try again.");
        setStatus("idle");
      }
    } catch (err) {
      console.error("Resend error:", err);
      message.error("Failed to send code. Please try again.");
      setStatus("idle");
    }
  };

  const renderStatusIndicator = () => {
    if (status === "sending") {
      return (
        <span className="status-text">
          <IconLoader2 className="spinning" size={14} /> Sending code...
        </span>
      );
    }
    if (status === "sent") {
      return (
        <span className="status-text success">
          <IconCheck size={14} /> Code sent!
        </span>
      );
    }
    if (status === "verifying") {
      return (
        <span className="status-text">
          <IconLoader2 className="spinning" size={14} /> Validating...
        </span>
      );
    }
    if (status === "expired" || seconds === 0) {
      return (
        <button className="resend-link" onClick={resendCode}>
          Request a new code
        </button>
      );
    }
    return <span className="status-text">Resend in {seconds}s</span>;
  };

  return (
    <Card id="phone">
      <div className="apply-form">
        <Headline>{pageSettings?.title}</Headline>
        <Paragraph opacity={0.6} align="left" size="14px">
          Enter the 6-digit code sent to {maskedDestination}
        </Paragraph>
        <Flex vertical style={{ marginTop: "24px" }}>
          <InputLabel>Verification Code:</InputLabel>
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={CODE_LENGTH}
            value={code}
            onChange={handleChange}
            className={`otp-single-input ${code.length === CODE_LENGTH ? "filled" : ""}`}
            disabled={status === "verifying"}
            autoFocus
            placeholder="------"
            style={{ "--otp-color": primaryColor }}
          />
          <div className="resend-row">{renderStatusIndicator()}</div>
        </Flex>
      </div>
    </Card>
  );
}

export default VerifyPhone;
